import {
    STREAMING_THEMES,
    getStreamingTheme,
    themesSupporting,
    StreamingThemePreview,
} from '../constants/streamingThemes';
import '../assets/css/StreamingThemePicker.css';
import { useId } from 'react';
import { normalizePlayerNameSize } from '../utils/streamingTypography';

const StreamingThemePicker = ({
    value,
    onChange,
    tournamentTitle,
    onTournamentTitleChange,
    displayBreaks,
    onDisplayBreaksChange,
    playerNameFormat = 'full',
    onPlayerNameFormatChange,
    playerNameSize = 100,
    onPlayerNameSizeChange,
    statsMode = 'auto',
    onStatsModeChange,
    disabled = false,
}) => {
    const nameSizeId = useId();
    const nameSize = normalizePlayerNameSize(playerNameSize);
    // `yellowtv` was briefly exposed as a fifth option. Treat saved instances as
    // the Welsh Open style so existing tables migrate without a broken picker.
    const normalizedValue = value === 'yellowtv'
        ? 'welshopen'
        : value === 'default'
            ? 'welshopen'
            : value;
    const matchedTheme = STREAMING_THEMES.find((theme) => theme.value === normalizedValue);
    const activeTheme = matchedTheme || getStreamingTheme('welshopen');
    const themesWithTitle = themesSupporting('supportsTitle');
    const showTitle = matchedTheme ? activeTheme.supportsTitle : true;
    const showBreaks = matchedTheme
        ? activeTheme.supportsBreaks && activeTheme.configurableBreaks !== false
        : true;
    const hasOptions = showTitle || showBreaks || Boolean(onPlayerNameFormatChange) || Boolean(onStatsModeChange) || Boolean(onPlayerNameSizeChange);
    const breaksValue = String(displayBreaks) === 'true' ? 'true' : 'false';
    // `theme` was the former automatic option. Treat it as full-name display so
    // old saved settings inherit the new, consistent default across every style.
    const nameFormatValue = playerNameFormat === 'last' ? 'last' : 'full';

    const selectTheme = (themeValue) => {
        if (disabled) return;

        // Only the style changes here. Every theme declares the same
        // `defaultNameFormat: 'full'`, so applying it could only ever overwrite a
        // deliberate surname-only choice - and because the value it wrote was
        // almost always the one already saved, the settings page read that second
        // update as "nothing changed" and cleared its unsaved-changes flag,
        // leaving Save disabled on the click that picked the style.
        onChange(themeValue);
    };

    return (
        <div className={`streaming-theme-picker${disabled ? ' is-disabled' : ''}`}>
            <label className="form-label">Streaming Theme</label>
            <div className="streaming-theme-grid" role="listbox" aria-label="Streaming theme">
                {STREAMING_THEMES.map((theme) => {
                    const isActive = matchedTheme?.value === theme.value;
                    return (
                        <div
                            key={theme.value}
                            className={`streaming-theme-card${isActive ? ' is-selected' : ''}`}
                            role="option"
                            tabIndex={disabled ? -1 : 0}
                            aria-selected={isActive}
                            aria-label={`${theme.label} - ${theme.name}`}
                            onClick={() => selectTheme(theme.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    selectTheme(theme.value);
                                }
                            }}
                        >
                            <StreamingThemePreview theme={theme} />
                            <div className="streaming-theme-card-meta">
                                <span className="streaming-theme-card-label">{theme.label}</span>
                                {isActive && <span className="badge bg-primary">Selected</span>}
                            </div>
                            <div className="streaming-theme-card-hint">{theme.hint}</div>
                            <div className="streaming-theme-card-caps">
                                <span className={theme.supportsBreaks ? 'is-supported' : 'is-unsupported'}>Breaks</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="hr-text hr-text-left mt-4 mb-3">
                {matchedTheme ? `${activeTheme.label} options` : 'Theme options'}
            </div>

            {!matchedTheme && (
                <div className="text-secondary mb-3">
                    Choose a streaming style. The current value is not one of the listed overlays.
                </div>
            )}

            {!hasOptions ? (
                <div className="text-secondary">
                    {activeTheme.label} ({activeTheme.name}) renders a fixed scorebar &mdash; there is nothing to configure here.
                </div>
            ) : (
                <div className="row">
                    {onStatsModeChange && (
                        <div className="col-md-12 mb-3">
                            <label className="form-label">
                                Snooker frame statistics
                                <select className="form-select mt-2" value={statsMode} disabled={disabled}
                                    onChange={event => onStatsModeChange(event.target.value)}>
                                    <option value="auto">Automatic + manual control</option>
                                    <option value="manual">Manual only</option>
                                    <option value="always">Always visible</option>
                                </select>
                            </label>
                            <div className="form-hint">Ahead/Behind, points remaining and reds on the table, on every snooker theme. Automatic shows up to 10 seconds each minute, giving recent scoring priority. Show or hide instantly from the referee page; the break returns afterward when Display Breaks is enabled.</div>
                        </div>
                    )}
                    {showTitle && (
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Tournament Title</label>
                            <input
                                type="text"
                                className="form-control"
                                value={tournamentTitle || ''}
                                onChange={(event) => onTournamentTitleChange(event.target.value)}
                                placeholder="Enter tournament title"
                                disabled={disabled}
                            />
                            <div className="form-hint">Shown on {themesWithTitle} only.</div>
                        </div>
                    )}
                    {showBreaks && (
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Display Breaks</label>
                            <select
                                className="form-select"
                                value={breaksValue}
                                onChange={(event) => onDisplayBreaksChange(event.target.value)}
                                disabled={disabled}
                            >
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                            </select>
                            <div className="form-hint">Shows the current break on the overlay.</div>
                        </div>
                    )}
                    {onPlayerNameFormatChange && (
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Player Names</label>
                            <select
                                className="form-select"
                                value={nameFormatValue}
                                onChange={(event) => onPlayerNameFormatChange(event.target.value)}
                                disabled={disabled}
                            >
                                <option value="full">Full name</option>
                                <option value="last">Last name only</option>
                            </select>
                            <div className="form-hint">Choose how player names appear in this overlay.</div>
                        </div>
                    )}
                    {onPlayerNameSizeChange && (
                        <div className="col-md-12 mb-3">
                            <div className="streaming-name-size-control">
                                <div className="streaming-name-size-heading">
                                    <label className="form-label mb-0" htmlFor={nameSizeId}>Player name size</label>
                                    <output htmlFor={nameSizeId}>{nameSize}%{nameSize === 100 ? ' · Default' : ''}</output>
                                </div>
                                <div className="streaming-name-size-slider">
                                    <span aria-hidden="true">Aa</span>
                                    <input id={nameSizeId} type="range" min="100" max="150" step="5"
                                        value={nameSize} disabled={disabled} aria-describedby={`${nameSizeId}-hint`}
                                        aria-valuetext={`${nameSize} percent${nameSize === 100 ? ', default' : ''}`}
                                        onChange={event => onPlayerNameSizeChange(Number(event.target.value))} />
                                    <span aria-hidden="true">Aa</span>
                                    <button type="button" className="btn btn-sm" disabled={disabled || nameSize === 100}
                                        onClick={() => onPlayerNameSizeChange(100)}>Reset size</button>
                                </div>
                                <div className="streaming-name-size-preview" aria-label="Player name size sample">
                                    <span style={{ fontSize: `${20 * nameSize / 100}px` }}>Player name</span>
                                    <small>Text size sample</small>
                                </div>
                                <div id={`${nameSizeId}-hint`} className="form-hint">
                                    Applies to both names on every streaming style. 100% keeps the original design; 150% is the largest.
                                    Long names may be shortened to fit—choose Last name only for more room.
                                    Save changes, then refresh your streaming page or OBS browser source to apply.
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default StreamingThemePicker;
